import { useDispatch, useSelector } from 'react-redux';
import useEmptyStates from './EmptyStates/useEmptyStates';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { formatCypressTag } from 'common/utils/cypress.utils';
import { useState } from 'react';
import { getAllTeamsGroupId, isMulti } from 'common/utils/utils';
import { FORMATTERS } from '../utils/formatters';
import useWidgetPropsByGraphType from '../BaseWidget/useWidgetPropsByGraphType';
import useCustomMetricPointContext from './useCustomWidgetPointContext';
import { selectTeamId } from 'state/actions';
import {
  GRAPH_REDIRECT,
  getCalculatedValue,
  getMetricsAvgForDisabledSeriesLegend
} from '../../custom-report.serivce';
import {
  isShowTotal,
  getGranularityByDates,
  shouldFetchTotalsFromBackend,
  isMedianMetric
} from '../../custom-report.util';
import { getPercentOffValue, getTotalsV3Value } from '../utils/metrics';
import { getMetricsGroupByV3 } from '../../custom-metrics-api.facade';
import { teamsSelector } from 'state/selectors/teams';

const useCustomWidget = ({
  graph,
  filters,
  widgetMetrics,
  events = {},
  editMode,
  shouldHideClickableMessage,
  isInspection,
  isPublic = true
}) => {
  const { name: title, unitType, goToSettingsType } = graph;

  const selectedTeam = useSelector(selectedTeamSelector);
  const dataCyPrefix = formatCypressTag(title);
  const [isHover, setIsHover] = useState(false);
  const dispatch = useDispatch();

  const teams = useSelector(teamsSelector) || [];
  const allTeamsGroupId = getAllTeamsGroupId(teams);

  const v3GroupBy = getMetricsGroupByV3({ ...filters, allTeamsGroupId });

  const { onAreaSelect, onPointHover, onPointClick } = events;

  const {
    metricsTotal,
    name: metricName,
    agg,
    loading: loadingMetrics,
    percentOff,
    data,
    avgByName
  } = widgetMetrics;

  const v3MetricName = isMedianMetric(metricName)
    ? [metricName, agg].filter(Boolean).join(':')
    : metricName;

  const shouldFetchBackendTotal = shouldFetchTotalsFromBackend(metricName);

  const metricsForAvg = shouldFetchBackendTotal
    ? [metricsTotal]
    : getMetricsAvgForDisabledSeriesLegend({
        graph,
        metrics: data,
        disabledSeriesIdsByMetricName: {}, // TODO: what should be here?
        metricsGroupByKey: v3GroupBy.key,
        isCustomPmMetrics: false
      });

  const percentOffValue = getPercentOffValue(
    percentOff,
    percentOff?.metrics?.[0],
    graph?.avgOnlyWhenDataAvailable,
    metricsForAvg
  );

  const average = shouldFetchBackendTotal
    ? getTotalsV3Value({
        percentOff,
        metricsForAvg,
        v3MetricsTotalValue: metricsTotal?.[v3MetricName]
      })
    : getCalculatedValue(
        metricName,
        avgByName,
        graph?.avgOnlyWhenDataAvailable,
        percentOffValue,
        metricsForAvg
      );

  const formattedAverage = unitType
    ? FORMATTERS[unitType]?.avgFormatter(average) ?? ''
    : '';

  const {
    isEmptyStateMetric,
    showNoDataInfoBox,
    missingGraphData,
    EmptyState
  } = useEmptyStates({
    loadingMetrics,
    graph,
    filters,
    subtext: formattedAverage
  });

  const contextFunctions = useCustomMetricPointContext({
    filters,
    selectedTeam,
    selectTeamId: id => dispatch(selectTeamId(id)),
    metrics: widgetMetrics.data,
    graph,
    metricName,
    editMode,
    isPublic
  });

  const graphProps = useWidgetPropsByGraphType({
    widgetMetrics,
    graph,
    filters,
    onPointClick: onPointClick || contextFunctions.default,
    onPointHover,
    onAreaSelect: onAreaSelect || contextFunctions.onAreaSelect,
    shouldHideClickableMessage
  });

  const {
    selectedContributor,
    globallySelectedTeams,
    firstGloballySelectedTeamId: globallySelectedTeamId,
    selectedGranularity: granularity
  } = filters;

  const selectedTeamId = isMulti(globallySelectedTeams)
    ? null
    : globallySelectedTeams?.[0]?.id;

  const benchmarkProps = {
    metricName,
    average,
    title,
    graph,
    selectedContributor,
    globallySelectedTeams,
    globallySelectedTeamId,
    selectedTeam,
    selectedTeamId,
    granularity: getGranularityByDates({
      selectedGranularity: granularity,
      ...(filters?.selectedTimeRanges || {})
    }),
    shouldShowBenchmarkIcon: !selectedContributor,
    calculationStrategy: agg
  };

  const headerProps = {
    title,
    articleId: graph.articleId,
    isHover,
    subtitle: false,
    showNoDataInfoBox,
    graph: { ...graph, metricsTotal },
    missingGraphData,
    dataCyPrefix,
    isEmptyStateMetric,
    formattedAverage,
    showTotal: isShowTotal({ filters, type: graph.metricType, isInspection }),
    filters,
    loadingMetrics,
    percentOffValue
  };

  return {
    headerProps,
    benchmarkProps,
    EmptyState,
    isEmptyStateMetric,
    isHover,
    setIsHover,
    missingGraphData,
    showNoDataInfoBox,
    widgetMetrics,
    graphProps,
    goToSettings: goToSettingsType ? GRAPH_REDIRECT[goToSettingsType] : {},
    loadingMetrics
  };
};

export default useCustomWidget;
